import React from "react";
import { Check } from "lucide-react";
import { cn } from "../../../lib/utils";

export interface DocumentType {
  value: string;
  label: string;
  description?: string;
}

interface DocumentTypeSelectorProps {
  title: string;
  options: DocumentType[];
  value?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

export const DocumentTypeSelector: React.FC<DocumentTypeSelectorProps> = ({
  title,
  options,
  value,
  onChange,
  disabled = false,
}) => {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
        {title}
      </label>

      <div className="space-y-2">
        {options.map((option) => {
          const isSelected = value === option.value;

          return (
            <button
              key={option.value}
              type="button"
              disabled={disabled}
              onClick={() => onChange(option.value)}
              className={cn(
                "w-full flex items-center gap-3 p-3",
                "rounded-lg border transition-all duration-150",
                "focus:outline-none",
                "text-left",
                isSelected
                  ? "border-gray-900 bg-gray-900 text-white"
                  : "border-gray-200 bg-white hover:border-gray-300",
                disabled && "cursor-not-allowed opacity-50"
              )}
            >
              {/* Radio indicator */}
              <div
                className={cn(
                  "flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center",
                  isSelected ? "border-white bg-white" : "border-gray-300"
                )}
              >
                {isSelected && (
                  <Check className="h-2.5 w-2.5 text-gray-900" strokeWidth={3} />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p
                  className={cn(
                    "text-sm font-medium",
                    isSelected ? "text-white" : "text-gray-900"
                  )}
                >
                  {option.label}
                </p>
                {option.description && (
                  <p
                    className={cn(
                      "text-xs",
                      isSelected ? "text-gray-300" : "text-gray-500"
                    )}
                  >
                    {option.description}
                  </p>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
